import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { RiskBadge } from '../components/RiskBadge';
import { Stat } from '../components/Stat';
import { Stars } from '../components/Stars';
import { Skel } from '../components/Skel';
import { Banner } from '../components/Banner';
import { Crumbs } from '../components/Crumbs';
import { EmptyState } from '../components/EmptyState';
import { LockedBlock } from '../components/LockedBlock';
import { VerdictHero } from '../components/VerdictHero';
import { EnforcementPanel } from '../components/EnforcementPanel';
import type {
  CompanyCard,
  RegistriesResponse,
  RiskExplain,
  DirectorsResponse,
  FoundersResponse,
  TaxRegimeResponse,
  VerdictResponse,
  RelatedResponse,
  EnforcementResult,
} from '../data/types';

interface CompanyProps {
  /** ИНН или slug компании. */
  slug: string;
}

/**
 * Страница карточки компании. Третья страница migration после Home + Search.
 *
 * Загружает в параллели:
 *  - GET /v1/companies/{slug}                    → основная карточка
 *  - GET /v1/companies/{slug}/risk/explain       → факторы риска (lazy при tab=risk)
 *  - GET /v1/companies/{slug}/registries         → Минцифры/FSA/СРО (lazy при tab=registries)
 *
 * Layout: Crumbs + header (название/ИНН/риск) + tabs (Обзор/Финансы/Реестры/Риск)
 *  + footer с PDF download.
 *
 * Mobile-first: clamp font, minmax grid, overflow-wrap:anywhere.
 */
/**
 * Seed initial verdict из inline __COMPANY_DATA__ (ADR-0015 cutover) — мгновенный
 * meaningful paint без double-fetch. null если payload нет / не тот slug / невалиден.
 */
function readInitialData(slug: string): VerdictResponse | null {
  if (typeof document === 'undefined') return null;
  const el = document.getElementById('__COMPANY_DATA__');
  if (!el?.textContent) return null;
  try {
    const p = JSON.parse(el.textContent);
    if (p?.schemaVersion !== 1 || !p.ssr) return null;
    const s = p.ssr;
    if (s.slug !== slug && s.inn !== slug) return null; // payload для другой страницы
    return { slug: s.slug, inn: s.inn, name: s.name, risk: s.risk, ...s.verdict };
  } catch {
    return null;
  }
}

export function Company({ slug }: CompanyProps) {
  const [card, setCard] = useState<CompanyCard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'overview' | 'finance' | 'registries' | 'enforcement' | 'risk'>(
    'overview'
  );
  const [registries, setRegistries] = useState<RegistriesResponse | null>(null);
  const [riskExplain, setRiskExplain] = useState<RiskExplain | null>(null);
  const [directors, setDirectors] = useState<DirectorsResponse | null>(null);
  const [founders, setFounders] = useState<FoundersResponse | null>(null);
  const [taxRegime, setTaxRegime] = useState<TaxRegimeResponse | null>(null);
  const [verdict, setVerdict] = useState<VerdictResponse | null>(() => readInitialData(slug));
  const [related, setRelated] = useState<RelatedResponse | null>(null);
  const [enforcement, setEnforcement] = useState<EnforcementResult | null>(null);
  const [enforcementError, setEnforcementError] = useState(false);

  // Основная карточка + вердикт. Вердикт seed из __COMPANY_DATA__ (cutover) —
  // мгновенный paint без double-fetch; иначе fetch.
  useEffect(() => {
    setCard(null);
    setError(null);
    const seed = readInitialData(slug);
    setVerdict(seed);
    const ac = new AbortController();
    api
      .company(slug, { signal: ac.signal })
      .then(setCard)
      .catch((e) => {
        if (e.name !== 'AbortError') {
          setError(e instanceof ApiError ? `API ${e.status}` : String(e));
        }
      });
    if (!seed) {
      api.verdict(slug, { signal: ac.signal }).then(setVerdict).catch(() => {});
    }
    return () => ac.abort();
  }, [slug]);

  // Cutover (ADR-0015): скрываем серверный SEO-shell ТОЛЬКО после meaningful render
  // (вердикт или карточка видны). При ошибке/незагрузке shell остаётся (бот + graceful).
  useEffect(() => {
    if ((verdict || card) && typeof document !== 'undefined') {
      document.documentElement.classList.add('pcp-react-ready');
    }
  }, [verdict, card]);

  // Загружаем directors, founders, taxRegime при появлении карточки
  useEffect(() => {
    if (!card) return;
    const ac = new AbortController();
    if (!directors) {
      api.directors(card.slug, { signal: ac.signal }).then(setDirectors).catch(() => {});
    }
    if (!founders) {
      api.founders(card.slug, { signal: ac.signal }).then(setFounders).catch(() => {});
    }
    if (!taxRegime) {
      api.taxRegime(card.slug, { signal: ac.signal }).then(setTaxRegime).catch(() => {});
    }
    if (!related) {
      api.related(card.slug, 8, { signal: ac.signal }).then(setRelated).catch(() => {});
    }
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.slug]);

  // Lazy-load registries при первом открытии tab=registries
  useEffect(() => {
    if (tab !== 'registries' || registries || !card) return;
    const ac = new AbortController();
    api
      .registries(card.slug, { signal: ac.signal })
      .then(setRegistries)
      .catch(() => {});
    return () => ac.abort();
  }, [tab, card, registries]);

  // Lazy-load исполнительных производств ФССП при первом открытии tab=enforcement
  useEffect(() => {
    if (tab !== 'enforcement' || enforcement || !card) return;
    const ac = new AbortController();
    api
      .enforcement(card.slug, { signal: ac.signal })
      .then(setEnforcement)
      .catch((e) => {
        if (e?.name !== 'AbortError') setEnforcementError(true);
      });
    return () => ac.abort();
  }, [tab, card, enforcement]);

  // Lazy-load risk-explain при tab=risk
  useEffect(() => {
    if (tab !== 'risk' || riskExplain || !card) return;
    const ac = new AbortController();
    api
      .riskExplain(card.slug, { signal: ac.signal })
      .then(setRiskExplain)
      .catch(() => {});
    return () => ac.abort();
  }, [tab, card, riskExplain]);

  if (error) {
    return (
      <main className="container" style={{ paddingTop: 32 }}>
        <Crumbs items={[{ label: 'Главная', href: '#/' }, { label: 'Поиск', href: '#/search' }, { label: slug }]} />
        <Banner tone="risk" title="Не удалось загрузить компанию">
          {error}
        </Banner>
      </main>
    );
  }

  if (!card) {
    return (
      <main className="container" style={{ paddingTop: 32 }}>
        <Crumbs items={[{ label: 'Главная', href: '#/' }, { label: 'Поиск', href: '#/search' }, { label: slug }]} />
        <Skel height={32} width="60%" style={{ marginTop: 16 }} />
        <Skel height={20} width="40%" style={{ marginTop: 8 }} />
        <Skel count={3} height={64} radius={12} style={{ marginTop: 24 }} />
      </main>
    );
  }

  const isInactive = card.status !== 'active';
  // Premium-гейтинг по token-флагу (M1 auth ещё нет — заглушка; реальный entitlement позже).
  // FREE: вердикт, негатив/санкции, базовый профиль, снимки. LOCKED: детальная доказательная база.
  const isAuthed = typeof window !== 'undefined' && !!window.localStorage?.getItem('pcp_access_token');
  return (
    <main className="container" style={{ paddingTop: 32 }}>
      <Crumbs
        items={[
          { label: 'Главная', href: '#/' },
          { label: 'Поиск', href: '#/search' },
          { label: card.name_short },
        ]}
      />

      {/* HEADER */}
      <header style={{ marginTop: 16, marginBottom: 24 }}>
        <h1
          style={{
            fontSize: 'clamp(24px, 3.5vw, 36px)',
            margin: 0,
            color: 'var(--navy-700, #0b1736)',
            overflowWrap: 'anywhere',
          }}
        >
          {card.name_short}
        </h1>
        <p
          style={{
            color: 'var(--muted, #6b7287)',
            fontSize: 14,
            marginTop: 4,
            overflowWrap: 'anywhere',
          }}
        >
          {card.name_full}
        </p>
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
            marginTop: 12,
            fontSize: 13,
          }}
        >
          <code
            style={{
              background: 'var(--surface-quiet, #f5f3ec)',
              padding: '4px 8px',
              borderRadius: 4,
              fontFamily: 'JetBrains Mono, monospace',
            }}
          >
            ИНН {card.inn}
          </code>
          <code
            style={{
              background: 'var(--surface-quiet, #f5f3ec)',
              padding: '4px 8px',
              borderRadius: 4,
              fontFamily: 'JetBrains Mono, monospace',
            }}
          >
            ОГРН {card.ogrn}
          </code>
          <RiskBadge level={card.risk.level} score={card.risk.score} />
          {taxRegime?.current && taxRegime.current.regime !== 'ОСНО' && (
            <span
              style={{
                background: '#e8f4e8',
                color: '#1f6b34',
                padding: '4px 10px',
                borderRadius: 999,
                fontWeight: 600,
                border: '1px solid #8fc99b',
                fontSize: 12,
              }}
            >
              {taxRegime.current.regime}
            </span>
          )}
          {card.signals.active_enforcement > 0 && (
            <span
              style={{
                background: '#fff0e0',
                color: '#9b5118',
                padding: '4px 10px',
                borderRadius: 999,
                fontWeight: 600,
                border: '1px solid #e6a86e',
                fontSize: 12,
              }}
            >
              ⚡ {card.signals.active_enforcement} исп. произв.
            </span>
          )}
          {card.sanctions.is_sanctioned && (
            <span
              style={{
                background: '#fde2e2',
                color: '#a31919',
                padding: '4px 10px',
                borderRadius: 999,
                fontWeight: 600,
                border: '1px solid #e89191',
              }}
            >
              ⚠ Под санкциями ({card.sanctions.active_listings})
            </span>
          )}
          {isInactive && (
            <span
              style={{
                background: '#fff5d9',
                color: '#7a5a10',
                padding: '4px 10px',
                borderRadius: 999,
                fontWeight: 600,
                border: '1px solid #e6c870',
              }}
            >
              {card.status === 'liquidated' ? 'Ликвидирована' : card.status}
            </span>
          )}
          {card.flags.has_special_tax_regime && (
            <TaxRegimeBadges flags={card.flags} />
          )}
          <Stars rating={card.reputation.rating} reviewsCount={card.reputation.reviews_count} />
        </div>
      </header>

      {/* ВЕРДИКТ СДЕЛКИ — центральный элемент (работать/осторожно/отказ) */}
      {verdict && <VerdictHero verdict={verdict} />}

      {/* TABS */}
      <div
        role="tablist"
        aria-label="Разделы карточки"
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: '1px solid var(--border, #e6e2d6)',
          marginBottom: 24,
          overflowX: 'auto',
        }}
      >
        {[
          { key: 'overview', label: 'Обзор' },
          { key: 'finance', label: 'Финансы' },
          { key: 'registries', label: 'Реестры' },
          { key: 'enforcement', label: 'Взыскания' },
          { key: 'risk', label: 'Риск' },
        ].map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            type="button"
            onClick={() => setTab(t.key as typeof tab)}
            style={{
              padding: '10px 16px',
              border: 'none',
              background: 'transparent',
              fontSize: 14,
              fontWeight: tab === t.key ? 600 : 500,
              color: tab === t.key ? 'var(--navy-700, #0b1736)' : 'var(--muted, #6b7287)',
              borderBottom: `2px solid ${tab === t.key ? 'var(--gold-500, #c79a2c)' : 'transparent'}`,
              cursor: 'pointer',
              flexShrink: 0,
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* TAB: OVERVIEW */}
      {tab === 'overview' && (
        <OverviewTab card={card} directors={directors} founders={founders} related={related} />
      )}

      {/* TAB: FINANCE */}
      {tab === 'finance' && <FinanceTab card={card} isAuthed={isAuthed} />}

      {/* TAB: REGISTRIES */}
      {tab === 'registries' && (
        <RegistriesTab registries={registries} />
      )}

      {/* TAB: ENFORCEMENT (ФССП исполнительные производства) */}
      {tab === 'enforcement' &&
        (enforcementError ? (
          <Banner tone="risk" title="Не удалось загрузить данные ФССП">
            Попробуйте обновить страницу позже.
          </Banner>
        ) : (
          <EnforcementPanel data={enforcement} loading={!enforcement} isAuthed={isAuthed} />
        ))}

      {/* TAB: RISK */}
      {tab === 'risk' && <RiskTab card={card} explain={riskExplain} isAuthed={isAuthed} />}

      {/* FOOTER actions */}
      <footer
        style={{
          marginTop: 40,
          paddingTop: 16,
          borderTop: '1px solid var(--border, #e6e2d6)',
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <a
          href={api.reportPdfUrl(card.slug)}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '10px 18px',
            background: 'var(--navy-700, #0b1736)',
            color: '#fff',
            textDecoration: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          📄 Скачать PDF отчёт
        </a>
        {card.last_synced_at && (
          <span style={{ color: 'var(--muted, #6b7287)', fontSize: 12 }}>
            Обновлено: {new Date(card.last_synced_at).toLocaleString('ru-RU')}
          </span>
        )}
      </footer>
    </main>
  );
}

function fmtMoney(v: string | null): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(1)} млрд ₽`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(1)} млн ₽`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(0)} тыс ₽`;
  return `${n.toLocaleString('ru-RU')} ₽`;
}

function OverviewTab({
  card,
  directors,
  founders,
  related,
}: {
  card: CompanyCard;
  directors: DirectorsResponse | null;
  founders: FoundersResponse | null;
  related: RelatedResponse | null;
}) {
  return (
    <section aria-label="Обзор">
      <div
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        }}
      >
        <Stat label="Возраст" value={`${card.age_years} лет`} format={false} />
        <Stat label="Регион" value={card.region.name ?? '—'} format={false} />
        <Stat label="Активные суды" value={card.signals.active_lawsuits} />
        <Stat label="Исп. производства" value={card.signals.active_enforcement} />
      </div>

      {/* Основные сведения */}
      <div
        style={{
          marginTop: 24,
          background: 'var(--surface, #ffffff)',
          border: '1px solid var(--border, #e6e2d6)',
          borderRadius: 12,
          padding: 20,
        }}
      >
        <h2
          style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: 18,
            margin: '0 0 12px',
            color: 'var(--navy-700, #0b1736)',
          }}
        >
          Основные сведения
        </h2>
        <Row label="Дата регистрации" value={new Date(card.registered_at).toLocaleDateString('ru-RU')} />
        <Row label="Правовая форма" value={card.legal_form ?? '—'} />
        <Row label="Адрес" value={card.address ?? '—'} />
        {card.okved.code && (
          <Row label="ОКВЭД" value={`${card.okved.code} · ${card.okved.name ?? ''}`} />
        )}
        {card.authorized_capital && (
          <Row label="Уставный капитал" value={`${Number(card.authorized_capital).toLocaleString('ru-RU')} ₽`} />
        )}
        <Row
          label="Руководитель"
          value={
            card.director?.full_name
              ? `${card.director.full_name}${card.director.position ? `, ${card.director.position}` : ''}`
              : '—'
          }
        />
        {card.director?.risk_flags?.is_mass_director && (
          <div style={{ marginTop: 8 }}>
            <Banner tone="warn" title="Массовый директор">
              Этот руководитель числится в {card.director.risk_flags.mass_companies_count}+ компаниях
            </Banner>
          </div>
        )}
      </div>

      {/* Финансовый снимок — FREE (полная динамика 5 лет во вкладке «Финансы») */}
      {(card.finance_summary.revenue || card.finance_summary.profit || card.finance_summary.employees != null) && (
        <div
          style={{
            marginTop: 16,
            background: 'var(--surface, #ffffff)',
            border: '1px solid var(--border, #e6e2d6)',
            borderRadius: 12,
            padding: 20,
          }}
        >
          <h2
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: 18,
              margin: '0 0 12px',
              color: 'var(--navy-700, #0b1736)',
            }}
          >
            Финансовые показатели{card.finance_summary.year ? ` (${card.finance_summary.year})` : ''}
          </h2>
          <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
            <Stat label="Выручка" value={fmtMoney(card.finance_summary.revenue)} format={false} />
            <Stat label="Чистая прибыль" value={fmtMoney(card.finance_summary.profit)} format={false} />
            {card.finance_summary.employees != null && (
              <Stat label="Сотрудники" value={card.finance_summary.employees} />
            )}
          </div>
          <p style={{ fontSize: 12, color: 'var(--muted, #6b7287)', marginTop: 10 }}>
            Полная динамика за 5 лет, коэффициенты и сравнение с отраслью — во вкладке «Финансы».
          </p>
        </div>
      )}

      {card.egrul_deep_summary && (
        <EgrulDeepBlock summary={card.egrul_deep_summary} />
      )}

      {/* Руководители (из ЕГРЮЛ) */}
      {directors === null ? (
        <Skel height={48} style={{ marginTop: 16 }} />
      ) : (directors.current.length > 0 || directors.history.length > 0) ? (
        <div
          style={{
            marginTop: 16,
            background: 'var(--surface, #ffffff)',
            border: '1px solid var(--border, #e6e2d6)',
            borderRadius: 12,
            padding: 20,
          }}
        >
          <h2
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: 18,
              margin: '0 0 12px',
              color: 'var(--navy-700, #0b1736)',
            }}
          >
            Руководители
          </h2>
          {directors.current.map((d, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                padding: '8px 0',
                borderBottom: '1px solid var(--border, #e6e2d6)',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                  {d.name ?? '—'}
                </div>
                <div style={{ fontSize: 13, color: 'var(--muted, #6b7287)' }}>
                  {d.position ?? 'Руководитель'}
                  {d.appointed_at
                    ? ` · с ${new Date(d.appointed_at).toLocaleDateString('ru-RU')}`
                    : ''}
                </div>
              </div>
              {d.inn && (
                <code
                  style={{
                    fontSize: 12,
                    color: 'var(--muted, #6b7287)',
                    fontFamily: 'JetBrains Mono, monospace',
                    alignSelf: 'center',
                    flexShrink: 0,
                  }}
                >
                  ИНН {d.inn}
                </code>
              )}
            </div>
          ))}
          {directors.history.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--muted, #6b7287)' }}>
                История ({directors.history.length})
              </summary>
              {directors.history.map((d, i) => (
                <div
                  key={i}
                  style={{
                    padding: '6px 0',
                    borderBottom: '1px solid var(--border, #e6e2d6)',
                    opacity: 0.75,
                  }}
                >
                  <span style={{ overflowWrap: 'anywhere' }}>{d.name ?? '—'}</span>
                  <span style={{ fontSize: 12, color: 'var(--muted, #6b7287)', marginLeft: 8 }}>
                    {d.position ?? ''}
                    {d.resigned_at
                      ? ` · до ${new Date(d.resigned_at).toLocaleDateString('ru-RU')}`
                      : ''}
                  </span>
                </div>
              ))}
            </details>
          )}
        </div>
      ) : null}

      {/* Учредители (из ЕГРЮЛ) */}
      {founders === null ? (
        <Skel height={48} style={{ marginTop: 16 }} />
      ) : founders.items.length > 0 ? (
        <div
          style={{
            marginTop: 16,
            background: 'var(--surface, #ffffff)',
            border: '1px solid var(--border, #e6e2d6)',
            borderRadius: 12,
            padding: 20,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              flexWrap: 'wrap',
              gap: 8,
              marginBottom: 12,
            }}
          >
            <h2
              style={{
                fontFamily: 'Fraunces, Georgia, serif',
                fontSize: 18,
                margin: 0,
                color: 'var(--navy-700, #0b1736)',
              }}
            >
              Учредители
            </h2>
            {founders.authorized_capital != null && (
              <span style={{ fontSize: 13, color: 'var(--muted, #6b7287)' }}>
                УК: {founders.authorized_capital.toLocaleString('ru-RU')} ₽
              </span>
            )}
          </div>
          {founders.items.slice(0, 10).map((f, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                padding: '8px 0',
                borderBottom: '1px solid var(--border, #e6e2d6)',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                  {f.name ?? '—'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted, #6b7287)' }}>
                  {FOUNDER_TYPE_LABELS[f.type] ?? f.type}
                  {f.inn ? ` · ИНН ${f.inn}` : ''}
                </div>
              </div>
              {(f.share_percent != null || f.share_amount != null) && (
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  {f.share_percent != null && (
                    <div
                      style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      {f.share_percent.toFixed(2)}%
                    </div>
                  )}
                  {f.share_amount != null && (
                    <div style={{ fontSize: 12, color: 'var(--muted, #6b7287)' }}>
                      {f.share_amount.toLocaleString('ru-RU')} ₽
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {founders.items.length > 10 && (
            <div style={{ marginTop: 8, fontSize: 13, color: 'var(--muted, #6b7287)' }}>
              … и ещё {founders.items.length - 10}
            </div>
          )}
        </div>
      ) : null}

      {/* Связанные компании — аффилированность (сигнал должной осмотрительности) */}
      <RelatedPreview related={related} />
    </section>
  );
}

function RelatedPreview({ related }: { related: RelatedResponse | null }) {
  if (!related) return null;
  const groups: Array<{ label: string; items: RelatedResponse['shared_director'] }> = [
    { label: 'По общему директору', items: related.shared_director },
    { label: 'По адресу регистрации', items: related.same_address },
  ];
  if (!groups.some((g) => g.items.length > 0)) return null;
  return (
    <div
      style={{
        marginTop: 16,
        background: 'var(--surface, #ffffff)',
        border: '1px solid var(--border, #e6e2d6)',
        borderRadius: 12,
        padding: 20,
      }}
    >
      <h2
        style={{
          fontFamily: 'Fraunces, Georgia, serif',
          fontSize: 18,
          margin: '0 0 4px',
          color: 'var(--navy-700, #0b1736)',
        }}
      >
        Связанные компании
      </h2>
      <p style={{ fontSize: 13, color: 'var(--muted, #6b7287)', margin: '0 0 12px' }}>
        Аффилированность по общему руководителю и адресу — сигнал должной осмотрительности.
      </p>
      {groups.map(
        (g) =>
          g.items.length > 0 && (
            <div key={g.label} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy-700, #0b1736)', marginBottom: 6 }}>
                {g.label} ({g.items.length})
              </div>
              {g.items.slice(0, 5).map((r, i) => (
                <a
                  key={i}
                  href={`#/company/${r.slug}`}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '8px 0',
                    borderBottom: '1px solid var(--border, #e6e2d6)',
                    textDecoration: 'none',
                    color: 'inherit',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{r.name_short}</span>
                  <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    {r.region_name && (
                      <span style={{ fontSize: 12, color: 'var(--muted, #6b7287)' }}>{r.region_name}</span>
                    )}
                    {r.risk_level && <RiskBadge level={r.risk_level} score={r.risk_score} />}
                  </span>
                </a>
              ))}
              {g.items.length > 5 && (
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--muted, #6b7287)' }}>
                  … и ещё {g.items.length - 5}
                </div>
              )}
            </div>
          )
      )}
    </div>
  );
}

function EgrulDeepBlock({
  summary,
}: {
  summary: NonNullable<CompanyCard['egrul_deep_summary']>;
}) {
  return (
    <div
      style={{
        marginTop: 16,
        background: 'var(--surface, #ffffff)',
        border: '1px solid var(--border, #e6e2d6)',
        borderRadius: 12,
        padding: 20,
      }}
    >
      <h2
        style={{
          fontFamily: 'Fraunces, Georgia, serif',
          fontSize: 18,
          margin: '0 0 12px',
          color: 'var(--navy-700, #0b1736)',
        }}
      >
        Сведения ЕГРЮЛ
      </h2>
      <div
        style={{
          display: 'grid',
          gap: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        }}
      >
        <Stat label="Учредителей" value={summary.founders_count} />
        <EgrulFlag label="Директор" value={summary.has_director} />
        <EgrulFlag label="Лицензии" value={summary.has_licenses} />
        <EgrulFlag label="Филиалы" value={summary.has_branches} />
      </div>
      <div style={{ marginTop: 10, fontSize: 12, color: 'var(--muted, #6b7287)' }}>
        Источник ЕГРЮЛ · обновлено {new Date(summary.fetched_at).toLocaleDateString('ru-RU')}
      </div>
    </div>
  );
}

function EgrulFlag({ label, value }: { label: string; value: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '10px 14px',
        background: value ? 'var(--surface-quiet, #f5f3ec)' : 'transparent',
        border: '1px solid var(--border, #e6e2d6)',
        borderRadius: 8,
      }}
    >
      <span style={{ fontSize: 12, color: 'var(--muted, #6b7287)' }}>{label}</span>
      <span style={{ fontWeight: 600, color: value ? 'var(--navy-700, #0b1736)' : 'var(--muted, #6b7287)' }}>
        {value ? '✓ Есть' : '—'}
      </span>
    </div>
  );
}

function TaxRegimeBadges({ flags }: { flags: CompanyCard['flags'] }) {
  if (!flags.has_special_tax_regime) return null;
  return (
    <span
      style={{
        background: '#e8f4e8',
        color: '#1f6b34',
        padding: '4px 10px',
        borderRadius: 999,
        fontWeight: 600,
        border: '1px solid #7ec89a',
        fontSize: 13,
      }}
    >
      📋 Спецрежим
    </span>
  );
}

function FinanceTab({ card, isAuthed }: { card: CompanyCard; isAuthed: boolean }) {
  if (!card.finance_summary.year) {
    return (
      <EmptyState
        icon="📊"
        title="Финансовые показатели недоступны"
        body="ФНС публикует данные с задержкой. Возможно, компания малое предприятие или не сдаёт отчётность."
      />
    );
  }
  return (
    <section aria-label="Финансы">
      <div
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          marginBottom: 24,
        }}
      >
        <Stat
          label="Выручка"
          value={fmtRevenue(card.finance_summary.revenue)}
          note={`за ${card.finance_summary.year} год`}
          format={false}
        />
        <Stat
          label="Прибыль"
          value={fmtRevenue(card.finance_summary.profit)}
          format={false}
        />
        <Stat
          label="Сотрудников"
          value={card.finance_summary.employees ?? '—'}
        />
      </div>
      {card.tax_summary && card.tax_summary.length > 0 && isAuthed && (
        <div
          style={{
            background: 'var(--surface, #ffffff)',
            border: '1px solid var(--border, #e6e2d6)',
            borderRadius: 12,
            padding: 20,
            overflowX: 'auto',
          }}
        >
          <h2
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: 18,
              margin: '0 0 12px',
              color: 'var(--navy-700, #0b1736)',
            }}
          >
            Уплаченные налоги (Прозрачный бизнес ФНС)
          </h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr>
                <th style={thStyle}>Год</th>
                <th style={thStyle}>Уплачено</th>
                <th style={thStyle}>НДС</th>
                <th style={thStyle}>Прибыль</th>
                <th style={thStyle}>Долг</th>
              </tr>
            </thead>
            <tbody>
              {card.tax_summary.map((t) => (
                <tr key={t.year}>
                  <td style={tdStyle}>{t.year}</td>
                  <td style={tdStyle}>{fmtRevenue(t.paid_total)}</td>
                  <td style={tdStyle}>{fmtRevenue(t.paid_vat)}</td>
                  <td style={tdStyle}>{fmtRevenue(t.paid_profit)}</td>
                  <td
                    style={{
                      ...tdStyle,
                      color: Number(t.debt_total) > 0 ? '#a31919' : 'var(--muted, #6b7287)',
                    }}
                  >
                    {fmtRevenue(t.debt_total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Премиум: детальная фин.история под LockedBlock (снимок выручки/прибыли — FREE в Обзоре) */}
      {card.tax_summary && card.tax_summary.length > 0 && !isAuthed && (
        <LockedBlock
          title="Полная финансовая история"
          description="Уплаченные налоги по годам (НДС, налог на прибыль), задолженность и динамика — в полном отчёте."
          ctaHref="#/pricing"
        />
      )}
    </section>
  );
}

function RegistriesTab({ registries }: { registries: RegistriesResponse | null }) {
  if (!registries) {
    return <Skel count={4} height={56} />;
  }
  const empty =
    registries.minsvyaz_summary.total === 0 &&
    registries.certificates_summary.total === 0 &&
    registries.sro_summary.total === 0;
  if (empty) {
    return (
      <EmptyState
        icon="📋"
        title="Записей в реестрах нет"
        body="Компания не входит в реестр отечественного ПО Минцифры, не имеет сертификатов Росаккредитации и не состоит в СРО."
      />
    );
  }

  return (
    <section aria-label="Реестры">
      {registries.minsvyaz_summary.total > 0 && (
        <RegistryBlock
          color="#1c4a99"
          icon="🔵"
          title="Реестр отечественного ПО (Минцифры)"
          summary={`${registries.minsvyaz_summary.active} активных из ${registries.minsvyaz_summary.total}`}
          rows={registries.minsvyaz_software.slice(0, 8).map((s) => ({
            primary: s.name,
            secondary: `Рег. № ${s.registration_number}${s.okpd_code ? ` · ОКПД ${s.okpd_code}` : ''}`,
            tertiary: s.inclusion_date
              ? `Включено ${new Date(s.inclusion_date).toLocaleDateString('ru-RU')}`
              : '',
            isActive: s.is_active,
          }))}
        />
      )}
      {registries.certificates_summary.total > 0 && (
        <RegistryBlock
          color="#1f6b34"
          icon="🟢"
          title="Сертификаты соответствия (Росаккредитация)"
          summary={`${registries.certificates_summary.valid} valid · ${registries.certificates_summary.certificates_count} серт. + ${registries.certificates_summary.declarations_count} декл.`}
          rows={registries.certificates.slice(0, 10).map((c) => ({
            primary: c.product_name ?? `Сертификат ${c.fsa_id}`,
            secondary: c.cert_type === 'certificate' ? 'Сертификат соответствия' : 'Декларация о соответствии',
            tertiary: `Выдан ${new Date(c.issued_at).toLocaleDateString('ru-RU')}${c.expires_at ? ` · до ${new Date(c.expires_at).toLocaleDateString('ru-RU')}` : ''}`,
            isActive: c.status === 'valid',
          }))}
        />
      )}
      {registries.sro_summary.total > 0 && (
        <RegistryBlock
          color="#5a2da8"
          icon="🟣"
          title="Членство в СРО"
          summary={`${registries.sro_summary.active} активных из ${registries.sro_summary.total}`}
          rows={registries.sro_membership.slice(0, 8).map((m) => ({
            primary: m.sro_name ?? '—',
            secondary: SRO_TYPE_LABELS[m.sro_type] ?? m.sro_type,
            tertiary: `Рег. № ${m.reg_number ?? '—'} · с ${new Date(m.joined_at).toLocaleDateString('ru-RU')}`,
            isActive: m.status === 'active',
          }))}
        />
      )}
    </section>
  );
}

function RiskTab({ card, explain, isAuthed }: { card: CompanyCard; explain: RiskExplain | null; isAuthed: boolean }) {
  if (!explain) {
    return <Skel count={5} height={48} />;
  }
  return (
    <section aria-label="Риск">
      <div
        style={{
          background: 'var(--surface, #ffffff)',
          border: '1px solid var(--border, #e6e2d6)',
          borderRadius: 12,
          padding: 20,
          marginBottom: 24,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
            marginBottom: 16,
          }}
        >
          <RiskBadge level={card.risk.level} score={card.risk.score} />
          <span style={{ fontSize: 13, color: 'var(--muted, #6b7287)' }}>
            Модель {explain.model_version} · обновлено{' '}
            {explain.calculated_at
              ? new Date(explain.calculated_at).toLocaleDateString('ru-RU')
              : '—'}
          </span>
        </div>
        <p
          style={{
            color: 'var(--text, #0e1320)',
            fontSize: 15,
            lineHeight: 1.5,
            marginBottom: 0,
          }}
        >
          {explain.interpretation}
        </p>
      </div>

      {explain.factors.length > 0 ? (
        <div>
          <h2
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: 18,
              margin: '0 0 12px',
              color: 'var(--navy-700, #0b1736)',
            }}
          >
            Сработавшие факторы ({explain.factors_count})
          </h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {(isAuthed ? explain.factors : explain.factors.slice(0, 2)).map((f) => (
              <li
                key={f.code}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '10px 12px',
                  background: 'var(--surface, #ffffff)',
                  border: '1px solid var(--border, #e6e2d6)',
                  borderRadius: 8,
                  marginBottom: 6,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {f.description}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--muted, #6b7287)',
                      fontFamily: 'JetBrains Mono, monospace',
                    }}
                  >
                    {f.code} · {f.group}
                  </div>
                </div>
                <span
                  style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontWeight: 600,
                    color:
                      f.weight >= 30
                        ? '#a31919'
                        : f.weight >= 15
                          ? '#9b5118'
                          : '#7a5a10',
                  }}
                >
                  +{f.weight}
                </span>
              </li>
            ))}
          </ul>
          {/* Полный разбор — премиум. Топ-факторы + итоговый уровень риска и вердикт — FREE. */}
          {!isAuthed && explain.factors.length > 2 && (
            <LockedBlock
              title="Полный разбор риска"
              description={`Ещё ${explain.factors.length - 2} из ${explain.factors_count} факторов с весами и вкладом в скоринг — в полном отчёте. Итоговый уровень риска и вердикт доступны выше бесплатно.`}
              ctaHref="#/pricing"
            />
          )}
        </div>
      ) : (
        <Banner tone="ok" title="Факторов риска не обнаружено">
          Базовые проверки пройдены. Риск-модель не нашла негативных сигналов.
        </Banner>
      )}
    </section>
  );
}

function RegistryBlock({
  color,
  icon,
  title,
  summary,
  rows,
}: {
  color: string;
  icon: string;
  title: string;
  summary: string;
  rows: Array<{
    primary: string;
    secondary: string;
    tertiary: string;
    isActive: boolean;
  }>;
}) {
  return (
    <div
      style={{
        background: 'var(--surface, #ffffff)',
        border: '1px solid var(--border, #e6e2d6)',
        borderLeft: `4px solid ${color}`,
        borderRadius: 8,
        padding: 16,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <h3
          style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: 16,
            margin: 0,
            color,
          }}
        >
          {icon} {title}
        </h3>
        <span style={{ fontSize: 13, color: 'var(--muted, #6b7287)' }}>{summary}</span>
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {rows.map((r, i) => (
          <li
            key={i}
            style={{
              padding: '8px 0',
              borderTop: i > 0 ? '1px solid var(--border, #e6e2d6)' : 'none',
              opacity: r.isActive ? 1 : 0.6,
            }}
          >
            <div style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{r.primary}</div>
            <div style={{ fontSize: 13, color: 'var(--muted, #6b7287)' }}>
              {r.secondary}
            </div>
            {r.tertiary && (
              <div style={{ fontSize: 12, color: 'var(--muted, #6b7287)' }}>
                {r.tertiary}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
        padding: '6px 0',
        borderBottom: '1px solid var(--border, #e6e2d6)',
        fontSize: 14,
      }}
    >
      <div style={{ color: 'var(--muted, #6b7287)', minWidth: 140, flexShrink: 0 }}>
        {label}
      </div>
      <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  borderBottom: '2px solid var(--gold-500, #c79a2c)',
  fontFamily: 'Manrope, sans-serif',
  fontSize: 13,
  color: 'var(--navy-700, #0b1736)',
  fontWeight: 600,
};
const tdStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid var(--border, #e6e2d6)',
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: 13,
};

const FOUNDER_TYPE_LABELS: Record<string, string> = {
  person: 'Физическое лицо',
  organization: 'Юридическое лицо',
  foreign: 'Иностранное лицо',
  government: 'Государство / муниципалитет',
};

const SRO_TYPE_LABELS: Record<string, string> = {
  builders: 'Строительство',
  designers: 'Изыскания и проектирование',
  surveyors: 'Изыскания',
  auditors: 'Аудит',
  appraisers: 'Оценка',
  arbitration_managers: 'Арбитражные управляющие',
  cadastral: 'Кадастровые инженеры',
  energy_audit: 'Энергоаудит',
  mediators: 'Медиаторы',
  actuaries: 'Актуарии',
  agro_revision: 'Агро-ревизия',
  other: 'Иное',
};

function fmtRevenue(value: string | null): string {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1_000_000_000_000)
    return `${(n / 1_000_000_000_000).toFixed(1)} трлн ₽`;
  if (Math.abs(n) >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} млрд ₽`;
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} млн ₽`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(0)} тыс ₽`;
  return `${n.toLocaleString('ru-RU')} ₽`;
}
